import { useState } from 'react';
import { MoreVertical, Trash2, Loader2, Pencil } from 'lucide-react';
import { useRouter } from '@tanstack/react-router';
import { useQueryClient } from '@tanstack/react-query';
import { Button } from '@/components/atoms/button';
import {
  DropdownMenu,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/atoms/dropdown-menu';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/atoms/alert-dialog';
import { OverflowMenuContent, RowOverflowMenuTrigger } from '@/components/molecules';
import { useApiMutation } from '@/hooks/useApiMutation';
import { apiDelete, operationsClient } from '@/lib/api';
import { operationsPricebookEndpoints } from '@/lib/api/endpoints';
import { membershipPlanKeys } from '@/lib/queryKeys';

export interface MembershipPlanActionsProps {
  id: string;
  name: string;
  variant: 'row' | 'header';
  /** Header: override default edit navigation. Row: ignored. */
  onEdit?: () => void;
  /** Navigate to list after archive. Defaults to true for header. */
  navigateAfterDelete?: boolean;
}

export function MembershipPlanActions({
  id,
  name,
  variant,
  onEdit,
  navigateAfterDelete,
}: MembershipPlanActionsProps) {
  const router = useRouter();
  const queryClient = useQueryClient();
  const [showConfirm, setShowConfirm] = useState(false);

  const shouldNavigateAfterDelete = navigateAfterDelete ?? variant === 'header';

  const deleteMutation = useApiMutation<void, string>({
    mutationFn: (pid) =>
      apiDelete<void>(operationsClient, operationsPricebookEndpoints.membershipPlan(pid)),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: membershipPlanKeys.all() });
      setShowConfirm(false);
      if (shouldNavigateAfterDelete) {
        // @ts-expect-error — search required by TanStack Router v1.168
        void router.navigate({ to: '/dashboard/membership-plans', search: (prev) => prev });
      }
    },
  });

  const handleEdit = () => {
    if (onEdit) {
      onEdit();
      return;
    }
    void router.navigate({
      to: '/dashboard/membership-plans/$id/edit',
      params: { id },
      // @ts-expect-error — search required by TanStack Router v1.168
      search: (prev) => prev,
    });
  };

  const handleConfirm = () => {
    deleteMutation.mutate(id);
  };

  if (variant === 'header') {
    return (
      <>
        <div className="hidden items-center gap-1.5 md:flex">
          <Button variant="outline" size="icon-sm" title="Edit" onClick={handleEdit}>
            <Pencil size={14} />
          </Button>
          <Button
            variant="outline"
            size="icon-sm"
            title="Archive"
            className="text-destructive border-destructive/40 hover:bg-destructive/10"
            onClick={() => setShowConfirm(true)}
          >
            <Trash2 size={14} />
          </Button>
        </div>
        <div className="md:hidden">
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline" size="icon-sm" title="Actions" aria-label="Actions">
                <MoreVertical size={14} />
              </Button>
            </DropdownMenuTrigger>
            <OverflowMenuContent>
              <DropdownMenuItem onSelect={handleEdit}>
                <Pencil className="mr-2 size-3.5" />
                Edit
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem
                onSelect={(e) => {
                  e.preventDefault();
                  setShowConfirm(true);
                }}
                className="text-destructive focus:text-destructive"
              >
                <Trash2 className="mr-2 size-3.5" />
                Archive
              </DropdownMenuItem>
            </OverflowMenuContent>
          </DropdownMenu>
        </div>
        <ArchiveDialog
          open={showConfirm}
          onOpenChange={setShowConfirm}
          onConfirm={handleConfirm}
          name={name}
          isPending={deleteMutation.isPending}
        />
      </>
    );
  }

  return (
    <>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <RowOverflowMenuTrigger />
        </DropdownMenuTrigger>
        <OverflowMenuContent>
          <DropdownMenuItem onSelect={handleEdit}>
            <Pencil className="mr-2 size-3.5" />
            Edit
          </DropdownMenuItem>
          <DropdownMenuSeparator />
          <DropdownMenuItem
            onSelect={(e) => {
              e.preventDefault();
              setShowConfirm(true);
            }}
            className="text-destructive focus:text-destructive"
          >
            <Trash2 className="mr-2 size-3.5" />
            Archive
          </DropdownMenuItem>
        </OverflowMenuContent>
      </DropdownMenu>
      <ArchiveDialog
        open={showConfirm}
        onOpenChange={setShowConfirm}
        onConfirm={handleConfirm}
        name={name}
        isPending={deleteMutation.isPending}
      />
    </>
  );
}

function ArchiveDialog({
  open,
  onOpenChange,
  onConfirm,
  name,
  isPending,
}: {
  open: boolean;
  onOpenChange: (v: boolean) => void;
  onConfirm: () => void;
  name: string;
  isPending: boolean;
}) {
  return (
    <AlertDialog open={open} onOpenChange={onOpenChange}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>Archive plan?</AlertDialogTitle>
          <AlertDialogDescription>
            Archive "{name}"? Existing members keep their benefits but no new members can join.
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel>Cancel</AlertDialogCancel>
          <AlertDialogAction
            onClick={(e) => {
              e.preventDefault();
              onConfirm();
            }}
            className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
          >
            {isPending ? (
              <Loader2 className="mr-1 size-3.5 animate-spin" />
            ) : (
              <Trash2 className="mr-1 size-3.5" />
            )}
            Archive
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
